<template>
    <div ref="rootRef" class="tw:relative">
        <button
            type="button"
            class="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-xl tw:bg-slate-900/90 tw:p-1.5 tw:text-left tw:transition-colors tw:hover:bg-slate-950/80"
            :class="open ? 'tw:bg-slate-950/80' : ''"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-label="triggerLabel"
            aria-haspopup="menu"
            :disabled="loading"
            data-testid="sidebar-user-card"
            @click="toggle"
        >
            <Skeleton
                v-if="loading"
                variant="tw:bg-transparent"
                padding="tw:p-0"
                custom-class="tw:w-full"
            >
                <template #skeleton>
                    <div class="tw:flex tw:items-center tw:gap-2.5 tw:min-w-0 tw:w-full" :class="compact ? 'tw:justify-center' : ''">
                        <SkeletonItem height="tw:h-10" custom-class="tw:w-10 tw:rounded-lg tw:shrink-0" />
                        <div v-if="!compact" class="tw:grid tw:gap-1.5 tw:flex-1 tw:min-w-0">
                            <SkeletonItem height="tw:h-3" custom-class="tw:w-24 tw:rounded" />
                            <SkeletonItem height="tw:h-2.5" custom-class="tw:w-20 tw:rounded" />
                        </div>
                    </div>
                </template>
            </Skeleton>

            <template v-else>
                <div class="tw:flex tw:flex-1 tw:items-center tw:gap-2.5 tw:min-w-0" :class="compact ? 'tw:justify-center' : ''">
                    <div
                        class="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:text-xs tw:font-bold tw:transition-[width,height] tw:duration-200"
                        :class="compact ? 'tw:w-10 tw:h-10' : 'tw:w-8 tw:h-8'"
                        :style="{ backgroundColor: clubColor, color: clubTextColor }"
                        data-testid="sidebar-user-avatar"
                    >{{ initials }}</div>
                    <transition name="admin-label">
                        <div v-if="!compact" class="tw:grid tw:flex-1 tw:min-w-0 tw:leading-tight">
                            <span class="tw:truncate tw:text-xs tw:font-semibold tw:text-white" data-testid="sidebar-user-name">{{ fullName }}</span>
                            <span v-if="role" class="tw:truncate tw:text-[11px] tw:font-medium tw:text-slate-300" data-testid="sidebar-user-role">{{ role }}</span>
                            <span v-else-if="email" class="tw:truncate tw:text-[11px] tw:font-medium tw:text-slate-300" data-testid="sidebar-user-email">{{ email }}</span>
                        </div>
                    </transition>
                </div>
                <i
                    v-if="!compact"
                    class="fas fa-ellipsis-v tw:ml-auto tw:shrink-0 tw:text-[14px] tw:text-slate-400"
                    aria-hidden="true"
                ></i>
            </template>
        </button>

        <div
            v-if="open"
            class="tw:z-50 tw:rounded-lg tw:border tw:border-slate-700 tw:bg-[#0d1527] tw:p-1.5 tw:shadow-xl tw:text-slate-200"
            :class="menuPositionClasses"
            role="menu"
            aria-label="Menú de usuario"
            data-testid="sidebar-user-card-menu"
        >
            <div class="tw:flex tw:items-center tw:gap-2 tw:px-1 tw:py-1.5">
                <div
                    class="tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:text-xs tw:font-bold"
                    :style="{ backgroundColor: clubColor, color: clubTextColor }"
                >{{ initials }}</div>
                <div class="tw:grid tw:flex-1 tw:min-w-0 tw:leading-tight">
                    <span class="tw:truncate tw:text-xs tw:font-semibold tw:text-white" data-testid="sidebar-user-menu-name">{{ fullName }}</span>
                    <span v-if="email || role" class="tw:truncate tw:text-[11px] tw:text-slate-400" data-testid="sidebar-user-menu-subtitle">{{ email || role }}</span>
                </div>
            </div>

            <div class="tw:my-1.5 tw:h-px tw:bg-slate-700" role="separator"></div>

            <button
                type="button"
                class="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:px-2 tw:py-1.5 tw:text-xs tw:font-medium tw:text-rose-300 tw:transition-colors tw:hover:bg-rose-500/10 tw:hover:text-rose-200"
                role="menuitem"
                data-testid="sidebar-user-logout"
                @click="handleLogout"
            >
                <i class="fas fa-sign-out-alt tw:w-4 tw:text-center" aria-hidden="true"></i>
                <span>Salir</span>
            </button>
        </div>
    </div>
</template>

<script setup>
    import { computed, ref } from 'vue';
    import { useAdminUser } from '@/composables/admin/layout/useAdminUser';
    import { useAdminClubIdentity } from '@/composables/admin/layout/useAdminClubIdentity';
    import { useHeaderDropdown } from '@/composables/admin/layout/useHeaderDropdown';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    const props = defineProps({
        compact: { type: Boolean, default: false }
    });
    const emit = defineEmits(['logout']);

    const { loading, fullName, email, initials, role } = useAdminUser();
    const { clubColor, clubTextColor } = useAdminClubIdentity();

    const rootRef = ref(null);
    const { open, toggle, close } = useHeaderDropdown(rootRef, { hover: false });

    const triggerLabel = computed(() => [fullName.value, role.value].filter(Boolean).join(' · ') || 'Menú de usuario');
    const menuPositionClasses = computed(() => {
        if (props.compact) {
            return 'tw:fixed tw:left-[84px] tw:bottom-5 tw:w-56';
        }
        return [
            'tw:absolute tw:bottom-full tw:right-0 tw:mb-2 tw:w-56',
            'tw:lg:fixed tw:lg:bottom-5 tw:lg:left-[260px] tw:lg:right-auto tw:lg:mb-0'
        ].join(' ');
    });

    function handleLogout() {
        close();
        emit('logout');
    }
</script>

<style scoped>
    .admin-label-enter-active,
    .admin-label-leave-active {
        transition: opacity 0.12s ease;
    }

    .admin-label-enter,
    .admin-label-leave-to {
        opacity: 0;
    }
</style>
